import { Trans, useLingui } from '@lingui/react/macro';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  DispenserFormOutput,
  DispenserFormValues,
  getDispenserFormOptions,
} from '@/features/dispensers/components/dispenser-form/config.ts';
import MenuItem from '@mui/material/MenuItem';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit.tsx';
import Stack from '@mui/material/Stack';
import { TwoColumnLayout } from './subcomponents/two-column-layout/TwoColumnLayout.tsx';
import Button from '@mui/material/Button';
import { FormErrors } from '@/common/components/form-errors/FormErrors.tsx';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { collectorQueries } from '@/features/collectors/queries.ts';
import { posQueries } from '@/features/pos/queries.ts';
import { PosCountryField } from './subcomponents/pos-country-field/PosCountryField.tsx';
import { FormNavigationBlocker } from '@/common/components/form-navigation-blocker/FormNavigationBlocker.tsx';
import { DispenserService } from '@/features/dispensers/services/DispenserService.ts';
import { ProducerField } from '@/features/dispensers/components/dispenser-form/subcomponents/producer-field/ProducerField.tsx';
import { useAppForm } from '@/lib/form.ts';
import { TypeField } from '@/features/dispensers/components/dispenser-form/subcomponents/type-field/TypeField.tsx';

export interface DispenserFormProps {
  edit?: boolean;
  initialValues?: DispenserFormValues;
  onSubmit: (values: DispenserFormOutput) => Promise<void>;
  onCancel: () => void;
}

export const DispenserForm = ({ initialValues, edit, onSubmit, onCancel }: DispenserFormProps) => {
  const { t } = useLingui();

  const form = useAppForm(getDispenserFormOptions(initialValues, onSubmit));

  const collectorListQuery = useSuspenseQuery(collectorQueries.list());
  const posListQuery = useSuspenseQuery(posQueries.list());

  const snValidationMutation = useMutation({
    mutationFn: (value: string) => DispenserService.validateSn({ sn: value }),
  });

  return (
    <Paper elevation={2} sx={{ maxWidth: '48rem' }}>
      <Box p={3}>
        <form.AppForm>
          <form.FormElement>
            <FormNavigationBlocker />
            <Stack gap={3}>
              <Typography variant="subtitle1">
                <Trans>Dispenser Information</Trans>
              </Typography>

              <FormErrors />

              <TwoColumnLayout>
                <form.AppField
                  name="sn"
                  asyncDebounceMs={500}
                  validators={{
                    onChangeAsync: async ({ value, fieldApi }) => {
                      if (!value || fieldApi.state.meta.isDefaultValue) return null;

                      const { isUnique } = await snValidationMutation.mutateAsync(value);
                      return isUnique ? null : t`Dispenser with this serial number already exists`;
                    },
                  }}
                >
                  {(field) => (
                    <field.TextField
                      required
                      label={t`Serial Number`}
                      isLoading={snValidationMutation.isPending}
                    />
                  )}
                </form.AppField>
                <form.AppField name="canisters">
                  {(field) => <field.NumberField required min={1} label={t`Canisters`} />}
                </form.AppField>
              </TwoColumnLayout>

              <TwoColumnLayout>
                <form.AppField name="pos">
                  {(field) => (
                    <field.SelectField label={t`Point of Sale`}>
                      <MenuItem value="">
                        <em>
                          <Trans>None</Trans>
                        </em>
                      </MenuItem>
                      {posListQuery.data.result.map((pos) => (
                        <MenuItem key={pos.id} value={pos.id.toString()}>
                          {pos.name}
                        </MenuItem>
                      ))}
                    </field.SelectField>
                  )}
                </form.AppField>
                <PosCountryField />
              </TwoColumnLayout>

              <TwoColumnLayout>
                <ProducerField form={form} />
                <TypeField form={form} />
              </TwoColumnLayout>

              <TwoColumnLayout>
                <form.AppField name="collectorId">
                  {(field) => (
                    <field.SelectField label={t`Collector`}>
                      <MenuItem value="">
                        <em>
                          <Trans>None</Trans>
                        </em>
                      </MenuItem>
                      {collectorListQuery.data.result.map((collector) => (
                        <MenuItem key={collector.id} value={collector.id}>
                          {collector.computerId}
                        </MenuItem>
                      ))}
                    </field.SelectField>
                  )}
                </form.AppField>
                <Box display={{ xs: 'none', sm: 'block' }} width="100%" />
              </TwoColumnLayout>

              <form.AppField name="note">
                {(field) => <field.TextField label={t`Note`} multiline minRows={3} />}
              </form.AppField>

              <Stack direction={{ xs: 'row-reverse', sm: 'row' }} gap={2}>
                <FormSubmit type="submit">
                  {edit ? <Trans>Save</Trans> : <Trans>Create</Trans>}
                </FormSubmit>
                <Button onClick={onCancel}>
                  <Trans>Cancel</Trans>
                </Button>
              </Stack>
            </Stack>
          </form.FormElement>
        </form.AppForm>
      </Box>
    </Paper>
  );
};
